import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import LeiderschapLayout from '@/components/LeiderschapLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { leiderschapApi } from '../api';
import SectionHeader from '../components/SectionHeader';
import ConfidentialBadge from '../components/ConfidentialBadge';
import MeetingFormDialog from '../components/MeetingFormDialog';
import { MEETING_STATUSES, MEETING_TYPES, findClassName, findLabel } from '../labels';
import type { LeadershipMeeting, LeadershipMeetingInput, MeetingStatus } from '../types';

export default function MeetingsPage() {
  const { hasCapability } = useAuth();
  const canManage = hasCapability('leiderschap_manage');

  const [meetings, setMeetings] = useState<LeadershipMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<MeetingStatus | 'all'>('all');
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchMeetings = async () => {
    try {
      const data = await leiderschapApi.listMeetings();
      setMeetings(data);
    } catch (error) {
      toast.error('Kon vergaderingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  const filteredMeetings = useMemo(
    () =>
      meetings
        .filter((m) => statusFilter === 'all' || m.status === statusFilter)
        .sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime()),
    [meetings, statusFilter],
  );

  const handleCreate = async (data: LeadershipMeetingInput) => {
    try {
      await leiderschapApi.createMeeting(data);
      toast.success('Vergadering aangemaakt');
      fetchMeetings();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Aanmaken mislukt');
      throw error;
    }
  };

  const handleDelete = async (meeting: LeadershipMeeting) => {
    if (!window.confirm(`Weet je zeker dat je "${meeting.title}" wilt verwijderen?`)) return;
    try {
      await leiderschapApi.deleteMeeting(meeting.id);
      toast.success('Vergadering verwijderd');
      fetchMeetings();
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  return (
    <LeiderschapLayout>
      <SectionHeader
        title="Vergaderingen"
        description="Plan vergaderingen, agenda, notulen en documenten."
        action={canManage && (
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuwe vergadering
          </Button>
        )}
      />

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={(value: MeetingStatus | 'all') => setStatusFilter(value)}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {MEETING_STATUSES.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {!loading && filteredMeetings.length === 0 && (
            <p className="p-6 text-sm text-slate-500">Geen vergaderingen gevonden.</p>
          )}
          <div className="divide-y divide-slate-100">
            {filteredMeetings.map((meeting) => (
              <div key={meeting.id} className="flex items-center justify-between gap-4 p-4">
                <Link to={`/leiderschap/vergaderingen/${meeting.id}`} className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{meeting.title}</p>
                    <Badge className={findClassName(MEETING_STATUSES, meeting.status)}>
                      {findLabel(MEETING_STATUSES, meeting.status)}
                    </Badge>
                    <ConfidentialBadge confidentiality={meeting.confidentiality} />
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {findLabel(MEETING_TYPES, meeting.type)} &middot;{' '}
                    {format(parseISO(meeting.scheduled_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                    {meeting.location_or_link ? ` · ${meeting.location_or_link}` : ''}
                  </p>
                </Link>
                {canManage && (
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(meeting)} aria-label="Verwijderen">
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <MeetingFormDialog open={dialogOpen} onOpenChange={setDialogOpen} meeting={null} onSubmit={handleCreate} />
    </LeiderschapLayout>
  );
}
